import { parse } from 'query-string';
import React     from 'react';

import { SkeletonPage }                          from './skeleton-page';
import * as Actions                              from '../../context/ctx-actions';
import { useGlobalCtx }                          from '../../context/ctx-hook';
import { PAGE_STATUS }                           from '../../helpers/constants/state-constants';
import { HELP_PHONE_PREMIUM, HELP_PHONE }        from '../../helpers/constants/constants';
import { createSubscriptionMap, filterSimpSubs } from '../../helpers/pure-functions';
import { formatFooterData }                      from '../../helpers/footer-data';
import * as NetworkCalls                         from '../../network/network-calls';

const getPaymentMethods = methodList =>
  methodList.reduce((acm, cur) => {
    if (cur.paymentMethodType === 'ACH' && cur.defaultForProductLines.includes('billmanager')) {
      return {
        ...acm,
        ach: cur
      };
    } else if (cur.defaultMethod) return {...acm, credit: cur};
    else return acm;
  }, {ach: null, credit: null});

export const US_SKUS = [
  '170166', 
  '170164', 
  '170165', 
  '170163', 
  '170329', 
  '170327', 
  '170330', 
  '170331', 
  '170415', 
  '170433', 
  '170434', 
  '170435',
];

const CA_SKUS = ['170195', '170194', '170193'];

const getPageData = async (hasFooter) =>
  await Promise.all([
    NetworkCalls.callGetUserInfo(),
    NetworkCalls.callGetUserProfile(),
    NetworkCalls.callGetSubscriptions(),
    NetworkCalls.callGetPaymentMethods(),
    NetworkCalls.callGetBillPayTopUp(),
    NetworkCalls.callGetTierPrices(),
    NetworkCalls.callGetHistories(),
    hasFooter ? NetworkCalls.callGetFooter() : null,
    NetworkCalls.callGetChatHours()
  ]);

const GetSiteData = () => {

  const {globalDispatch} = useGlobalCtx();

  const getSiteData = React.useCallback(async () => {
    try {
      const QS_PARMS = parse(window.location.search, {parseNumbers: true});

      const COUNTRY    = QS_PARMS?.site ?? 'us';
      const HIDE_NAV   = QS_PARMS['hide-nav'] ? QS_PARMS['hide-nav'] === 1 : false;
      const IS_EMBED   = QS_PARMS.embed ? QS_PARMS.embed === 1 : false;
      const HAS_FOOTER = !HIDE_NAV && !IS_EMBED;

      globalDispatch(Actions.setCtxField('pageStatus', PAGE_STATUS.LOADING));

      const [userInfo, profile, subscription, methods, topUps, tierPrices, histories, footer, chatHours] = await getPageData(HAS_FOOTER);

      const HISTORY_MAP = histories.data.reduce((acm, {line, ...rest}) => {
        if (!acm[line]) acm[line] = [];
        return {...acm, [line]: [...acm?.[line], rest]};
      }, {});

      const SUB_MAP = filterSimpSubs(createSubscriptionMap(subscription.data, COUNTRY));

      let SKUS = COUNTRY === 'ca' ? CA_SKUS : US_SKUS;
      if (SUB_MAP.acme) SKUS = SKUS.concat(SUB_MAP.acme.renewalSku);

      let {data: skuData} = await NetworkCalls.callGetSkuInfo(SKUS.join(','));

      if (SUB_MAP.acme) {
        let SKU     = SUB_MAP.acme.renewalSku;
        let newSkus = [];

        if (SKU && skuData[SKU].RenewalSKU && skuData[SKU].RenewalSKU !== SKU) {
          newSkus = [skuData[SKU].RenewalSKU];
        }

        if (SKU && skuData[SKU].SwitchMonthlyAnnualSKU && skuData[SKU].SwitchMonthlyAnnualSKU !== SKU) {
          newSkus = [...newSkus, skuData[SKU].SwitchMonthlyAnnualSKU];
        }

        if (newSkus.length > 0) {
          const SWITCH_CALL = await NetworkCalls.callGetSkuInfo(newSkus.join(','));
          skuData           = {...skuData, ...SWITCH_CALL.data};
        }
      }

      globalDispatch(Actions.mergeMainStateObj({
        pageStatus:    PAGE_STATUS.READY,
        userId:        profile.data.id,
        subscriptions: SUB_MAP,
        skus:          skuData,
        tierPrices:    tierPrices.data[COUNTRY],
        country:       COUNTRY,
        payment:       getPaymentMethods(methods.data),
        footer:        HAS_FOOTER ? formatFooterData(footer.data) : null,

        personalInfo: {
          firstName: profile.data?.firstName ?? '',
          lastName:  profile.data?.lastName ?? '',
          fullName:  profile.data?.primaryAddress?.fullName ?? '',
          line1:     profile.data?.primaryAddress?.line1 ?? '',
          line2:     profile.data?.primaryAddress?.line2 ?? '',
          city:      profile.data?.primaryAddress?.city ?? '',
          state:     profile.data?.primaryAddress?.state ?? '',
          country:   profile.data?.primaryAddress?.country ?? '',
          zipCode:   profile.data?.primaryAddress?.zipCode ?? ''
        },

        security: {
          quickenId: userInfo.data.username,
          phone:     userInfo.data.phone,
          mfa:       userInfo.data.defaultChannel
        },

        ui: {
          clientId:         QS_PARMS?.clientId ?? null,
          hasFooter:        HAS_FOOTER,
          hasDBOffer:       !!SUB_MAP[`quicken-${COUNTRY}`],
          hasSubscriptions: Object.keys(SUB_MAP).length > 0,
          supportPhone:     userInfo?.data?.entitlements?.split(',').includes('PREMIUM_SUPPORT') ? HELP_PHONE_PREMIUM : HELP_PHONE,

          embed:         QS_PARMS['embed'] ?? false,
          hideNav:       QS_PARMS['hide-nav'] ?? false,
          showDownloads: QS_PARMS['show-downloads'] !== undefined ? !!QS_PARMS['show-downloads'] : true,
          chatHours:     chatHours.data
        },

        billManager: {...topUps.data},

        history: HISTORY_MAP
      }));
    } catch (err) {
      console.log(err);
      globalDispatch(Actions.setCtxField('pageStatus', PAGE_STATUS.HAS_ERROR));
    }
  }, [globalDispatch]);

  React.useEffect(() => {
    getSiteData();
  }, [getSiteData]);

  return <SkeletonPage/>;
};

GetSiteData.displayName = 'GetSiteData';

export { GetSiteData };
